import { useState, useCallback, useMemo } from 'react';
import { useAdminI18n } from '@/lib/i18n/use-admin-i18n';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import { useUsersPaginated, useDeleteUser } from '@/lib/hooks/useUsers';
import { usePermission } from '@/lib/rbac/usePermission';
import { DynamicTable } from '@/components/custom/table/DynamicTable';
import type {
  DynamicTableColumnConfig,
  DynamicTableControllers,
} from '@/components/custom/table/dynamic-table-types';
import type { User } from '@cms/models';
import {
  AdminTableRowActions,
  PagePrimaryActionButton,
} from '@/components/common/AdminTableRowActions';
import { SearchFilter, StatusFilter } from '@/components/custom/filters';
import { UserFormModal, type UserFormModalMode } from './UserFormModal';
type StatusFilterValue = string;

interface UsersTableProps {
  rows: User[];
  controllers?: DynamicTableControllers;
  rowOffset: number;
  onView: (user: User) => void;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  isDeleting: boolean;
  isLoading: boolean;
  canView: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

function UsersTable(props: Readonly<UsersTableProps>) {
  const {
    rows,
    controllers,
    rowOffset,
    onView,
    onEdit,
    onDelete,
    isDeleting,
    isLoading,
    canView,
    canUpdate,
    canDelete,
  } = props;
  const { col, act, page, statusOptions, tableLoading } = useAdminI18n();

  const columns: DynamicTableColumnConfig<User>[] = useMemo(
    () => [
    {
      id: 'no',
      header: col('no'),
      columnType: 'number',
      cell: ({ index }) => rowOffset + index + 1,
      widthMode: 'fit',
      size: 64,
      meta: { className: 'w-16 text-muted-foreground' },
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: col('name'),
      sortable: true,
      columnType: 'text',
      cell: ({ value }) => (
        <span className="font-medium text-foreground">{(value as string) || '—'}</span>
      ),
    },
    {
      id: 'email',
      accessorKey: 'email',
      header: col('email'),
      sortable: true,
      columnType: 'text',
      cell: ({ value }) => (
        <span className="text-muted-foreground">{value as string}</span>
      ),
    },
    {
      id: 'role',
      header: col('role'),
      columnType: 'text',
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.role?.name ?? '—'}</span>
      ),
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: col('status'),
      sortable: true,
      columnType: 'badge',
      displayLabelOptions: statusOptions,
      widthMode: 'fit',
      size: 120,
      meta: { className: 'min-w-[120px]' },
    },
    {
      id: 'actions',
      header: col('actions'),
      columnType: 'actions',
      cell: ({ row }) => (
        <AdminTableRowActions
          act={act}
          onView={canView ? () => onView(row) : undefined}
          onEdit={canUpdate ? () => onEdit(row) : undefined}
          onDelete={canDelete ? () => onDelete(row) : undefined}
          isDeleting={isDeleting}
        />
      ),
      widthMode: 'fit',
      size: 220,
      minSize: 180,
      meta: { className: 'min-w-[180px] text-right' },
    },
    ],
    [act, canDelete, canUpdate, canView, col, isDeleting, onDelete, onEdit, onView, rowOffset, statusOptions],
  );

  return (
    <DynamicTable<User>
      features={{
        columns: true,
        rows: true,
        sorting: true,
        pagination: true,
        globalFilter: false,
        stripedRows: true,
        fontSize: 'xs',
        compactPadding: true,
      }}
      columns={columns}
      rows={{
        data: rows,
        getRowId: row => row.id,
      }}
      controllers={controllers}
      isLoading={isLoading}
      loadingMessage={tableLoading}
      emptyMessage={page('users', 'empty')}
    />
  );
}

export function UsersPage() {
  const { page, statusOptions, allStatusesOption } = useAdminI18n();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'email', desc: false }]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('');
  const [formModal, setFormModal] = useState<{
    open: boolean;
    mode: UserFormModalMode;
    user: User | null;
  }>({ open: false, mode: 'create', user: null });

  const canCreate = usePermission('user:create');
  const canView = usePermission('user:read');
  const canUpdate = usePermission('user:update');
  const canDelete = usePermission('user:delete');

  const currentPage = pagination.pageIndex + 1;
  const pageSize = pagination.pageSize;

  const sortBy =
    sorting[0]?.id === 'name' ||
    sorting[0]?.id === 'email' ||
    sorting[0]?.id === 'status' ||
    sorting[0]?.id === 'createdAt'
      ? (sorting[0].id as 'name' | 'email' | 'status' | 'createdAt')
      : 'email';
  const sortOrder = sorting[0]?.desc ? 'desc' : 'asc';

  const { data: listResponse, isLoading } = useUsersPaginated({
    page: currentPage,
    pageSize,
    search: globalFilter.trim() || undefined,
    status: statusFilter || undefined,
    sortBy,
    sortOrder,
    platformUsersOnly: true,
  });

  const users = listResponse?.data ?? [];
  const totalRecords = listResponse?.total ?? 0;
  const deleteMutation = useDeleteUser();

  const pageCount = Math.max(1, Math.ceil(totalRecords / pageSize));
  const safePageIndex = Math.min(pagination.pageIndex, pageCount - 1);
  const pageState: PaginationState = {
    pageIndex: safePageIndex,
    pageSize,
  };
  const rowOffset = safePageIndex * pageSize;

  const handleDelete = useCallback(
    async (user: User) => {
      if (!globalThis.confirm(`Are you sure you want to delete user "${user.email}"?`)) {
        return;
      }
      try {
        await deleteMutation.mutateAsync(user.id);
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } } };
        alert(err.response?.data?.message ?? 'Failed to delete user');
      }
    },
    [deleteMutation]
  );

  const controllers: DynamicTableControllers = {
    sort: {
      state: sorting,
      onSortingChange: updater => {
        setSorting(prev => (typeof updater === 'function' ? updater(prev) : updater));
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
      },
    },
    pagination: {
      state: pageState,
      pageCount,
      rowCount: totalRecords,
      onPaginationChange: updater => {
        setPagination(prev => (typeof updater === 'function' ? updater(prev) : updater));
      },
    },
  };

  return (
    <div className="space-y-2 p-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">{page('users', 'title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{page('users', 'subtitle')}</p>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2">
            <PagePrimaryActionButton
              onClick={() => setFormModal({ open: true, mode: 'create', user: null })}
            >
              {page('users', 'new')}
            </PagePrimaryActionButton>
          </div>
        )}
      </div>

      <div className="glass-card overflow-hidden">
        <div className="flex flex-wrap items-end gap-2 border-b border-border p-3">
          <SearchFilter
            id="users-search"
            label="Search"
            value={globalFilter}
            onChange={v => {
              setGlobalFilter(v);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            placeholder={page('users', 'search_placeholder')}
            className="[&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
          <StatusFilter
            id="users-status"
            label="Status"
            value={statusFilter}
            onChange={v => {
              setStatusFilter(v as StatusFilterValue);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            options={statusOptions}
            emptyOption={allStatusesOption}
            className="[&_button]:h-9 [&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
        </div>
        <UsersTable
          rows={users}
          controllers={controllers}
          rowOffset={rowOffset}
          onView={u => setFormModal({ open: true, mode: 'view', user: u })}
          onEdit={u => setFormModal({ open: true, mode: 'edit', user: u })}
          onDelete={handleDelete}
          isDeleting={deleteMutation.isPending}
          isLoading={isLoading}
          canView={canView}
          canUpdate={canUpdate}
          canDelete={canDelete}
        />
      </div>

      <UserFormModal
        open={formModal.open}
        onClose={() => setFormModal(prev => ({ ...prev, open: false, user: null }))}
        mode={formModal.mode}
        user={formModal.user}
        onSuccess={() => setFormModal(prev => ({ ...prev, open: false, user: null }))}
      />
    </div>
  );
}
